"use client";

import React, { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useQuery } from "@tanstack/react-query";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import type { InternetPlanDetailData } from "@/lib/fetch/internet";
import InternetPlanDetail from "./InternetPlanDetail";
import { useInternetCompare } from "./use-internet-compare";

type Props = {
  planId: string | null;
  onOpenChange: (open: boolean) => void;
};

/**
 * Side drawer with one plan's full record, fetched from the public plan
 * endpoint when it opens. The last id is kept while the drawer animates shut
 * so the content does not flash back to a skeleton.
 */
const InternetPlanSheet: React.FC<Props> = ({ planId, onOpenChange }) => {
  const t = useTranslations("internet");
  const locale = useLocale();
  const compare = useInternetCompare();
  const [activeId, setActiveId] = useState<string | null>(planId);

  useEffect(() => {
    if (planId) setActiveId(planId);
  }, [planId]);

  const query = useQuery({
    queryKey: ["resource", "internet", "plan", activeId, locale],
    enabled: Boolean(activeId),
    staleTime: 1000 * 60 * 15,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async () => {
      const params = new URLSearchParams({
        id: activeId ?? "",
        language: locale,
      });
      const res = await fetch(
        `/api/site/public/resource/internet/plan?${params}`,
        { method: "GET", headers: { "Content-Type": "application/json" } },
      );
      if (!res.ok) throw new Error("Failed to load plan");
      const result = (await res.json()) as {
        success: boolean;
        data: InternetPlanDetailData | null;
      };
      if (!result.success || !result.data) {
        throw new Error("Failed to load plan");
      }
      return result.data;
    },
  });

  return (
    <Sheet open={Boolean(planId)} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-xl lg:max-w-2xl"
      >
        <SheetHeader className="sr-only">
          <SheetTitle>
            {query.data
              ? `${query.data.provider.name} ${query.data.name}`
              : t("detail.loading")}
          </SheetTitle>
          <SheetDescription>
            {query.data?.text.description.slice(0, 160) ?? t("detail.loading")}
          </SheetDescription>
        </SheetHeader>

        {query.isError ? (
          <div className="flex flex-col gap-3 p-6">
            <p className="text-sm text-muted-foreground">{t("detail.error")}</p>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="w-fit text-sm font-semibold text-blue-700 hover:underline"
            >
              {t("detail.retry")}
            </button>
          </div>
        ) : query.data ? (
          <InternetPlanDetail
            data={query.data}
            compare={{
              selected: compare.has(query.data.id),
              disabled: compare.isFull,
              onToggle: compare.toggle,
            }}
          />
        ) : (
          <div className="flex flex-col gap-4 p-6" aria-busy>
            <div className="flex gap-2">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="mt-4 h-40 w-full rounded-2xl" />
            <Skeleton className="h-64 w-full rounded-2xl" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default InternetPlanSheet;
